// ContentIQ — Core Configuration (walking skeleton).
//
// The core layer shared by every content project: Product Catalog, Core Voice,
// Personas, Output Types, Brand, and the Approved Content library. Phase 0
// renders live (empty) data read-only; editing + seed data arrive in Phase 1
// ("Core config tool"). Admin-only pilot.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DrilldownLayout from '../../components/DrilldownLayout';
import {
  FONTS, HUES, INK, NEUTRAL, RADII, STATE,
  buttonDisabled, buttonLink, buttonPrimarySmall, buttonSmall, displayFont,
} from '../../utils/wizardTheme';

const API = process.env.REACT_APP_API_URL;

const emptyNote = { color: INK.secondary, fontSize: '13px' };
const th = { textAlign: 'left', padding: '6px 10px', borderBottom: `2px solid ${NEUTRAL.border}`, fontSize: '12px', color: INK.secondary, whiteSpace: 'nowrap' };
const td = { padding: '6px 10px', borderBottom: `1px solid ${NEUTRAL.borderSoft}`, fontSize: '13px', verticalAlign: 'top' };

const DOC_STYLES = `
  .ciq-doc { font-size: 13px; color: ${INK.body}; line-height: 1.55; }
  .ciq-doc h1 { font-size: 18px; margin: 0 0 10px; font-family: ${FONTS.display}; color: ${INK.primary}; }
  .ciq-doc h2 { font-size: 15px; margin: 18px 0 6px; border-bottom: 1px solid ${NEUTRAL.border}; padding-bottom: 3px; font-family: ${FONTS.display}; color: ${INK.primary}; }
  .ciq-doc h3 { font-size: 13.5px; margin: 14px 0 4px; color: ${INK.primary}; }
  .ciq-doc table { border-collapse: collapse; margin: 8px 0; max-width: 100%; }
  .ciq-doc th, .ciq-doc td { border: 1px solid ${NEUTRAL.border}; padding: 4px 8px; text-align: left; vertical-align: top; }
  .ciq-doc th { background: ${NEUTRAL.band}; }
  .ciq-doc code { background: ${NEUTRAL.chipBg}; padding: 0 4px; border-radius: 3px; font-size: 12px; }
  .ciq-doc blockquote { border-left: 3px solid ${NEUTRAL.border}; margin: 8px 0; padding-left: 10px; color: ${INK.secondary}; }
  .ciq-doc ul, .ciq-doc ol { padding-left: 22px; }
  .ciq-doc hr { border: none; border-top: 1px solid ${NEUTRAL.border}; margin: 14px 0; }
`;

function CoreDocPanel({ docKey }) {
  const [doc, setDoc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    axios.get(`${API}/contentiq/catalog/core-docs/${docKey}`)
      .then(r => setDoc(r.data))
      .catch(() => setFailed(true));
  }, [docKey]);
  if (failed) return <div style={emptyNote}>Couldn't load this document.</div>;
  if (!doc) return <div style={emptyNote}>Loading…</div>;
  return (
    <div>
      <div style={{
        background: STATE.done.tint, border: `1px solid ${STATE.done.border}`, borderRadius: RADII.small,
        padding: '8px 12px', fontSize: '12px', color: STATE.done.deep, marginBottom: '12px',
      }}>
        This document <strong>is</strong> the live guidance — versioned in git, edited via PR
        (backend <code>app/contentiq/core_docs/{doc.filename}</code>), deployed with the code.
        Version: <code title={doc.sha256}>{doc.sha256.slice(0, 12)}</code>
      </div>
      <style>{DOC_STYLES}</style>
      {/* Sanctioned dangerouslySetInnerHTML: our own repo markdown, PR-reviewed,
          rendered server-side — never user/supplier input. */}
      <div className="ciq-doc" dangerouslySetInnerHTML={{ __html: doc.content_html }} />
    </div>
  );
}

function Phase1Note({ children }) {
  return (
    <div style={{
      background: HUES.strategy.tint, border: `1px solid ${HUES.strategy.border}`, borderRadius: RADII.small,
      padding: '8px 12px', fontSize: '12px', color: HUES.strategy.text, marginBottom: '12px',
    }}>
      {children}
    </div>
  );
}

function PageStructureEditor({ format, onSaved }) {
  // The configurator (Edward, 2026-08-11): each format's REAL page sequence —
  // e.g. CS is promotion+reg then asset/thank-you, DA is interactive LP then
  // reg then asset. Strategy generation mirrors these rows exactly, so edits
  // here change every future generation for the format.
  const [pages, setPages] = useState((format.page_structure || []).map(p => ({ ...p })));
  const [branching, setBranching] = useState(!!format.has_branching);
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState('');

  const edit = (i, field, value) => { setPages(ps => ps.map((p, pi) => (pi === i ? { ...p, [field]: value } : p))); setDirty(true); };
  const save = async () => {
    setNote('');
    try {
      await axios.put(`${API}/contentiq/catalog/formats/${format.id}`, { page_structure: pages, has_branching: branching });
      setDirty(false); setNote('Saved.');
      setTimeout(() => setNote(''), 2000);
      onSaved();
    } catch (e) { setNote(e.response?.data?.detail || 'Save failed.'); }
  };

  return (
    <div style={{ background: NEUTRAL.band, border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.small, padding: '10px 12px', margin: '4px 0 10px' }}>
      <div style={{ fontSize: '12px', color: INK.secondary, marginBottom: '6px' }}>
        Page sequence for <strong>{format.format_name}</strong> — strategy documents follow these pages exactly.
      </div>
      {pages.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', margin: '3px 0' }}>
          <span style={{ fontSize: '12px', color: INK.muted, width: '18px' }}>{i + 1}.</span>
          <input value={p.name || ''} onChange={e => edit(i, 'name', e.target.value)} placeholder="Page name"
            style={{ padding: '4px 6px', fontSize: '12px', width: '240px', fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small }} />
          <input value={p.description || ''} onChange={e => edit(i, 'description', e.target.value)} placeholder="What this page does"
            style={{ padding: '4px 6px', fontSize: '12px', flex: 1, fontFamily: FONTS.body, border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small }} />
          <button onClick={() => { setPages(ps => ps.filter((_, pi) => pi !== i)); setDirty(true); }}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: INK.muted }}>✕</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '6px' }}>
        <button onClick={() => { setPages(ps => [...ps, { name: '', description: '' }]); setDirty(true); }}
          style={buttonSmall}>+ Add page</button>
        <label style={{ fontSize: '12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={branching} onChange={e => { setBranching(e.target.checked); setDirty(true); }} />{' '}
          Supports interactive branching
        </label>
        <button onClick={save} disabled={!dirty}
          style={dirty
            ? { ...buttonPrimarySmall(HUES.strategy.main), padding: '3px 10px', fontSize: '12px' }
            : { ...buttonPrimarySmall(), padding: '3px 10px', fontSize: '12px', ...buttonDisabled }}>
          Save rules
        </button>
        {note && <span style={{ fontSize: '12px', color: note === 'Saved.' ? STATE.done.text : STATE.danger.text }}>{note}</span>}
      </div>
    </div>
  );
}

// R2.1: the structured recipe (backend docs/contentiq-r2-strategy-recipe-schema.md).
// Slice 1 is deliberately a PREVIEW + validated JSON editor — the server
// validates on save (structural errors 400 and nothing is stored; flexed-
// convention warnings return as recipe_warnings and save anyway). A nicer
// form-based editor can follow once the recipes settle.
function StrategyRecipeEditor({ format, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [warnings, setWarnings] = useState([]);
  const recipe = format.strategy_recipe;

  const beginEdit = () => {
    setDraft(JSON.stringify(recipe || {}, null, 2));
    setNote(''); setWarnings([]); setEditing(true);
  };
  const save = async () => {
    setNote('');
    let parsed;
    try { parsed = JSON.parse(draft); }
    catch (e) { setNote(`Not valid JSON: ${e.message}`); return; }
    try {
      const r = await axios.put(`${API}/contentiq/catalog/formats/${format.id}`,
        { strategy_recipe: parsed });
      setWarnings(r.data.recipe_warnings || []);
      setNote('Saved.');
      setEditing(false);
      onSaved();
    } catch (e) { setNote(e.response?.data?.detail || 'Save failed.'); }
  };

  const bantSummary = (() => {
    const bant = recipe?.questions?.bant;
    if (!bant) return null;
    const placement = bant.placement || {};
    const parts = Object.entries(placement).map(([k, dims]) =>
      `${k}: ${Array.isArray(dims) ? dims.join('+') : dims}`);
    const implied = Object.entries(bant.implied || {}).map(([d, src]) => `${d} implied by ${src}`);
    return [...parts, ...implied].join(' · ') || 'none anywhere';
  })();

  return (
    <div style={{ background: NEUTRAL.band, border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.small, padding: '10px 12px', margin: '4px 0 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: INK.body }}>
          Strategy recipe — the structured spec the generator reads (R2)
        </span>
        <span style={{ flexGrow: 1 }} />
        {!editing && <button onClick={beginEdit} style={buttonSmall}>{recipe ? '✎ Edit recipe JSON' : '+ Add recipe'}</button>}
      </div>
      {!recipe && !editing && (
        <div style={{ fontSize: '12px', color: INK.muted }}>
          No structured recipe yet — the generator falls back to the legacy prose fields.
        </div>
      )}
      {recipe && !editing && (
        <div style={{ fontSize: '12px', color: INK.body, display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {recipe.paths && (
            <div><b>Paths:</b> one asset per {recipe.paths.unit}, selected by{' '}
              {recipe.paths.selector === 'routing_question' ? 'a routing question (branching)' : 'media targeting'}
              {recipe.paths.count?.typical ? ` · typically ${recipe.paths.count.typical}` : ''}</div>
          )}
          {recipe.touches && <div><b>Touches:</b> {recipe.touches.count} sequential outreaches to the same lead</div>}
          {(recipe.touchpoints || []).map(tp => (
            <div key={tp.key} style={{ paddingLeft: '8px' }}>
              {tp.kind === 'phone' ? '📞' : '📄'} <b>{tp.name}</b>
              <span style={{ color: INK.muted }}>
                {' '}— {tp.sequence}{tp.per_path ? ' · per path' : ''}
                {tp.program_stage ? ` · program stage ${tp.program_stage}` : ''}
                {recipe.lead_shares?.[String(tp.program_stage)] !== undefined && tp.kind === 'phone'
                  ? ` · ${Math.round(recipe.lead_shares[String(tp.program_stage)] * 100)}% of leads` : ''}
                {' · '}{(tp.bricks || []).map(b => b.set || b.register || b.type).join(', ')}
              </span>
            </div>
          ))}
          {bantSummary && <div><b>BANT:</b> {bantSummary}</div>}
          {recipe.script && (
            <div><b>Script:</b> {recipe.script.variant_default}, {recipe.script.placement === 'inline_split' ? 'inside the stage blocks' : 'its own block'}
              {recipe.script.present_default ? ' · present by default' : ' · per work order'}</div>
          )}
        </div>
      )}
      {editing && (
        <div>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={18} spellCheck={false}
            style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: '11.5px', border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small, padding: '8px' }} />
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
            <button onClick={save} style={{ ...buttonPrimarySmall(HUES.strategy.main), padding: '3px 10px', fontSize: '12px' }}>Save recipe</button>
            <button onClick={() => { setEditing(false); setNote(''); }} style={buttonSmall}>Cancel</button>
          </div>
        </div>
      )}
      {note && <div style={{ fontSize: '12px', marginTop: '6px', color: note === 'Saved.' ? STATE.done.text : STATE.danger.text }}>{note}</div>}
      {warnings.length > 0 && (
        <div style={{ fontSize: '11.5px', marginTop: '4px', color: STATE.gate.text }}>
          {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}
    </div>
  );
}

function FormatsPanel() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = () => axios.get(`${API}/contentiq/catalog/formats`).then(r => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (rows === null) return <div style={emptyNote}>Loading…</div>;
  return (
    <div>
      <Phase1Note>
        The table below is the machine-read spec (seeded, drives the create form's derived format).
        ⚙ Rules opens the per-product configurator — the page sequence every strategy document
        for that format must follow. The full prose catalog follows below.
      </Phase1Note>
      {rows.length === 0 ? (
        <div style={emptyNote}>No product formats yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={th}>Format</th><th style={th}>Funnel Stage</th><th style={th}>Pages</th>
                <th style={th}>Assets</th><th style={th}>Branching</th><th style={th}>Recipe</th><th style={th}>Production Status</th><th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map(f => (
                <React.Fragment key={f.id}>
                  <tr>
                    <td style={{ ...td, fontWeight: 600 }}>{f.format_name}</td>
                    <td style={td}>{f.funnel_stage}</td>
                    <td style={td}>{(f.page_structure || []).length ? (f.page_structure || []).map(p => p.name).join(' → ') : f.landing_page_count}</td>
                    <td style={td}>{f.asset_count}</td>
                    <td style={td}>{f.has_branching ? 'Yes' : 'No'}</td>
                    <td style={td}>{f.strategy_recipe ? '✓ structured' : '— legacy prose'}</td>
                    <td style={td}>{f.production_status}</td>
                    <td style={td}>
                      <button onClick={() => setOpenId(openId === f.id ? null : f.id)}
                        style={buttonSmall}>
                        {openId === f.id ? '▲ Close' : '⚙ Rules'}
                      </button>
                    </td>
                  </tr>
                  {openId === f.id && (
                    <tr><td colSpan={8} style={{ padding: 0 }}>
                      <StrategyRecipeEditor format={f} onSaved={load} />
                      <PageStructureEditor format={f} onSaved={load} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: '14px' }}>
        <CoreDocPanel docKey="product-catalog" />
      </div>
    </div>
  );
}

function VoicePanel() {
  return <CoreDocPanel docKey="core-voice" />;
}

function OutputTypesPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/contentiq/catalog/output-types`).then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  if (rows === null) return <div style={emptyNote}>Loading…</div>;
  return (
    <div>
      <Phase1Note>
        Each output type carries its prerequisite chain — what must exist before it can be generated. This is the data
        behind Specific Output mode. Phase 1 seeds the four mapped outputs (Overview/Analysis Doc, Recommended Assets,
        Landing Page Copy, Sales Enablement Deck).
      </Phase1Note>
      {rows.length === 0 ? (
        <div style={emptyNote}>No output types yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr><th style={th}>Output</th><th style={th}>Format</th><th style={th}>Active</th></tr>
            </thead>
            <tbody>
              {rows.map(o => (
                <tr key={o.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{o.output_name}</td>
                  <td style={td}>{o.file_format}</td>
                  <td style={td}>{o.is_active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const libInput = {
  border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.small, padding: '5px 8px',
  fontSize: '13px', fontFamily: FONTS.body, color: INK.primary, background: NEUTRAL.pageBg,
};

function fmtBytes(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

function AnnotationCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  if (!editing) {
    return (
      <span
        onClick={() => { setDraft(row.annotation || ''); setEditing(true); }}
        style={{ cursor: 'pointer', color: row.annotation ? INK.body : INK.muted }}
        title="Click to edit — this is the 'why it's a good example' note the model reads"
      >
        {row.annotation || '+ why is this a good example?'}
      </span>
    );
  }
  const save = () => {
    setBusy(true);
    axios.patch(`${API}/contentiq/catalog/approved-content/${row.id}`, { annotation: draft })
      .then(r => { onSaved(r.data); setEditing(false); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <textarea
        value={draft} onChange={e => setDraft(e.target.value)} rows={3}
        style={{ ...libInput, width: '100%', resize: 'vertical' }} autoFocus
      />
      <span style={{ display: 'flex', gap: '6px' }}>
        <button style={buttonPrimarySmall()} onClick={save} disabled={busy}>Save</button>
        <button style={buttonSmall} onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
      </span>
    </span>
  );
}

function ApprovedContentPanel() {
  const [rows, setRows] = useState(null);
  const [outputTypes, setOutputTypes] = useState([]);
  const [formats, setFormats] = useState([]);
  const [clients, setClients] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [outputTypeId, setOutputTypeId] = useState('');
  const [formatId, setFormatId] = useState('');
  const [clientId, setClientId] = useState('');
  const [annotation, setAnnotation] = useState('');
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contentiq/catalog/approved-content`).then(r => setRows(r.data)).catch(() => setRows([]));
    // Output types keep their served order on purpose (wizard/delivery order);
    // formats and clients sort alphabetically (Edward, 2026-08-31).
    axios.get(`${API}/contentiq/catalog/output-types`).then(r => setOutputTypes(r.data)).catch(() => {});
    axios.get(`${API}/contentiq/catalog/formats`)
      .then(r => setFormats([...r.data].sort((a, b) => (a.format_name || '').localeCompare(b.format_name || ''))))
      .catch(() => {});
    axios.get(`${API}/clients/`)
      .then(r => setClients([...r.data].sort((a, b) => (a.name || '').localeCompare(b.name || ''))))
      .catch(() => {});
  }, []);

  const nameOf = (list, id, key) => (list.find(x => x.id === id) || {})[key] || '—';

  const pickFile = (f) => {
    setFile(f || null);
    if (f && !title.trim()) setTitle(f.name.replace(/\.[a-z0-9]+$/i, ''));
  };

  const upload = () => {
    if (!file) return;
    setBusy(true); setErr(null);
    const fd = new FormData();
    fd.append('file', file);
    if (title.trim()) fd.append('title', title.trim());
    if (outputTypeId) fd.append('output_type_id', outputTypeId);
    if (formatId) fd.append('product_format_id', formatId);
    if (clientId) fd.append('client_id', clientId);
    if (annotation.trim()) fd.append('annotation', annotation.trim());
    axios.post(`${API}/contentiq/catalog/approved-content`, fd)
      .then(r => {
        setRows(prev => [r.data, ...(prev || [])]);
        // Wipe the whole form for the next file (Edward, 2026-08-31) — the
        // file input is uncontrolled, so bumping its key is what clears it.
        setFile(null); setTitle(''); setAnnotation('');
        setOutputTypeId(''); setFormatId(''); setClientId('');
        setFileKey(k => k + 1);
      })
      .catch(e => setErr(e.response?.data?.detail || `Upload failed (${e.response?.status || 'network'})`))
      .finally(() => setBusy(false));
  };

  const download = (row) => {
    axios.get(`${API}/contentiq/catalog/approved-content/${row.id}/download`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = row.original_filename || row.title;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setErr("Couldn't download that file."));
  };

  const remove = (row) => {
    if (!window.confirm(`Remove "${row.title}" from the library? Future generations stop using it.`)) return;
    axios.delete(`${API}/contentiq/catalog/approved-content/${row.id}`)
      .then(() => setRows(prev => prev.filter(x => x.id !== row.id)))
      .catch(() => setErr("Couldn't remove that entry."));
  };

  const patchRow = (updated) => setRows(prev => prev.map(x => (x.id === updated.id ? updated : x)));

  if (rows === null) return <div style={emptyNote}>Loading…</div>;
  return (
    <div>
      <Phase1Note>
        Real delivered documents, curated as examples. Every Content Strategy and Telequal Script generation
        reads the newest matching examples of its own output type (same product format preferred) and matches
        their register and structure — with a hard rule never to mention an example's client in its output.
        Changes apply to the NEXT generation; nothing regenerates on its own. The "why it's a good example"
        note is read by the model too, so say what to imitate.
      </Phase1Note>

      <div style={{
        border: `1px solid ${NEUTRAL.border}`, borderRadius: RADII.small, padding: '12px',
        marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '640px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: INK.primary }}>Add an example</div>
        <input key={fileKey} type="file" onChange={e => pickFile(e.target.files && e.target.files[0])} style={{ fontSize: '13px' }} />
        <input
          type="text" placeholder="Title" value={title} onChange={e => setTitle(e.target.value)}
          style={libInput}
        />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <select value={outputTypeId} onChange={e => setOutputTypeId(e.target.value)} style={libInput}>
            <option value="">Output type…</option>
            {outputTypes.map(o => <option key={o.id} value={o.id}>{o.output_name}</option>)}
          </select>
          <select value={formatId} onChange={e => setFormatId(e.target.value)} style={libInput}>
            <option value="">Product format (optional)…</option>
            {formats.map(f => <option key={f.id} value={f.id}>{f.format_name}</option>)}
          </select>
          <select value={clientId} onChange={e => setClientId(e.target.value)} style={libInput}>
            <option value="">Client (optional)…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <textarea
          placeholder="Why is this a good example? (the model reads this)" value={annotation}
          onChange={e => setAnnotation(e.target.value)} rows={2}
          style={{ ...libInput, resize: 'vertical' }}
        />
        {err && <div style={{ color: STATE.danger.deep, fontSize: '12px' }}>{err}</div>}
        <div>
          <button
            style={busy || !file || !outputTypeId ? { ...buttonPrimarySmall(), ...buttonDisabled } : buttonPrimarySmall()}
            onClick={upload}
            disabled={busy || !file || !outputTypeId}
            title={!outputTypeId ? 'Pick which output type this is an example of' : undefined}
          >
            {busy ? 'Uploading…' : '⬆ Add to library'}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={emptyNote}>Library is empty — add the delivered samples from Content IQ/Sample Outputs to start grounding generation.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={th}>Title</th><th style={th}>Output type</th><th style={th}>Format</th>
                <th style={th}>Client</th><th style={th}>Size</th><th style={th}>Text</th>
                <th style={{ ...th, minWidth: '220px' }}>Why it's a good example</th><th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map(a => (
                <tr key={a.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{a.title}</td>
                  <td style={td}>{nameOf(outputTypes, a.output_type_id, 'output_name')}</td>
                  <td style={td}>{nameOf(formats, a.product_format_id, 'format_name')}</td>
                  <td style={td}>{nameOf(clients, a.client_id, 'name')}</td>
                  <td style={td}>{fmtBytes(a.size_bytes)}</td>
                  <td style={td} title={a.has_text ? `${a.text_chars.toLocaleString()} characters extracted` : 'No text could be extracted — this entry is stored but never used as an example'}>
                    {a.has_text ? '✓' : <span style={{ color: STATE.danger.deep }}>✗ none</span>}
                  </td>
                  <td style={td}><AnnotationCell row={a} onSaved={patchRow} /></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button style={buttonSmall} onClick={() => download(a)} title="Download the original file">⬇</button>{' '}
                    <button style={buttonSmall} onClick={() => remove(a)} title="Remove from the library">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ContentIQAdmin() {
  const navigate = useNavigate();
  const [section, setSection] = useState('formats');
  const sections = [
    { key: 'formats', label: 'Product Catalog', description: 'Format specs the wizard reads when building the strategy', content: <FormatsPanel /> },
    { key: 'voice', label: 'Core Voice', description: 'Versioned house voice principles', content: <VoicePanel /> },
    { key: 'personas', label: 'Persona Playbook', description: 'How personas + message maps get built', content: <CoreDocPanel docKey="persona-playbook" /> },
    { key: 'output-types', label: 'Output Types', description: 'Deliverables + their prerequisite chains', content: <OutputTypesPanel /> },
    { key: 'brand', label: 'Deck Playbook & Brand', description: 'Sales enablement deck process + brand spec', content: <CoreDocPanel docKey="deck-playbook" /> },
    { key: 'approved', label: 'Approved Content Library', description: 'Annotated examples that ground generation', content: <ApprovedContentPanel /> },
  ];
  return (
    <div style={{ padding: '20px 24px', maxWidth: '1100px', fontFamily: FONTS.body, color: INK.primary }}>
      <button onClick={() => navigate('/contentiq')} style={{ ...buttonLink, fontSize: '13px', marginBottom: '8px' }}>
        ← ContentIQ Home
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
        <h1 style={{ margin: 0, ...displayFont('24px', 700) }}>ContentIQ config</h1>
      </div>
      <p style={{ color: INK.secondary, fontSize: '13px', marginTop: 0 }}>
        Shared knowledge every content project draws on. Changes here affect future projects — never in-flight ones (see Core Voice versioning).
      </p>
      <DrilldownLayout
        sections={sections}
        selectedKey={section}
        onSelect={setSection}
        sidebarLabel="Core Layer"
      />
    </div>
  );
}
